/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import styled from '@emotion/styled';
import * as size from '@constant/size';
import * as color from '@constant/color';
import * as text from '@constant/text';
import * as design from '@constant/design';
import TextBlock from 'component/common/block/text-block';
import AuthBox from 'component/auth/auth-box';
import Gap from '@component/common/gap';
import TextInput from '@component/common/input/text-input';
import IconBlock from '@component/common/block/icon-block';
import ImageTextBlock from '@component/common/block/image-text-block';
import { createAuthLogoText, createErrorText } from '@util/string';
import { createAuthLogoImage } from '@util/image';
import Term from '@component/auth/term';
import { useAuthInput, useSignUpAndIn } from '@hook/auth/useAuth';
import { SERVICE } from '@config/environment';
import { EAuthType } from '@datatype/type';

const PasswordContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
`;

const Signup = () => {
  const { password, email, username } = useAuthInput();
  const { oauthSignup, inhouseSignup, isSigning } = useSignUpAndIn();

  const authType = useTypedSelector((state) => state.rootReducer?.authReducer?.authType) ?? null;
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) ?? null;
  const isSignupTerm = useTypedSelector((state) => state.rootReducer?.authReducer?.isSignupTerm) ?? false;
  const isMarketingTerm = useTypedSelector((state) => state.rootReducer?.authReducer?.isMarketingTerm) ?? false;

  const isSignupAvailable =
    !isSigning && isSignupTerm && username.value && (authType === EAuthType.INHOUSE ? password.value : true);

  const handleSignUpClick = () => {
    if (!isSignupAvailable || !authType) return;
    if (authType === EAuthType.INHOUSE) {
      inhouseSignup(username.value, email.value, password.value, isMarketingTerm);
    } else {
      oauthSignup(authType, username.value, email.value, isMarketingTerm);
    }
  };

  return (
    <AuthBox design={design.AUTH_BOX_DESIGN}>
      <ImageTextBlock
        src={createAuthLogoImage()}
        alt='otterbrick'
        text={createAuthLogoText()}
        design={design.AUTH_LOGO_DESIGN}
      />
      <Gap height={size.MARGIN_2} />
      {authType !== 'inhouse' && (
        <>
          <TextBlock
            text={'연결된 계정'}
            design={{
              text: { size: 'medium', bold: true },
              box: { width: '100%', alignX: 'left' },
            }}
          />
          <TextBlock
            text={email.value}
            design={{
              text: { size: 'medium', bold: false, oneline: true },
              box: { width: '100%', alignX: 'left' },
            }}
          />
          <Gap height={size.MARGIN_1} />
        </>
      )}
      <TextInput
        name={'username'}
        placeholder={'아이디'}
        design={design.AUTH_INPUT_DESIGN}
        onChange={username.handler}
        value={username.value}
      />
      {authType === 'inhouse' && (
        <>
          <Gap height={size.MARGIN_1} />
          <PasswordContainer>
            <TextInput
              name={'password'}
              placeholder={'비밀번호'}
              design={design.PASSWORD_INPUT_DESIGN}
              onChange={password.handler}
              value={password.value}
              type={password.visible ? 'text' : 'password'}
            />
            <IconBlock
              icon={password.visible ? 'visibility-on' : 'visibility-off'}
              design={{
                icon: { size: 'medium', kind: 'outlined', color: color.GRAY_1 },
                box: { right: size.MARGIN_1, event: { cursor: 'pointer' } },
              }}
              click={{ handler: password.visibleHandler }}
            />
          </PasswordContainer>
        </>
      )}
      <TextBlock
        text={createErrorText(errorCode)}
        design={{
          ...design.ERROR_TEXT_DESIGN,
          box: { ...design.ERROR_TEXT_DESIGN, height: size.ERROR_TEXT_HEIGHT_2, alignX: 'left' },
        }}
      />
      <Term />
      <Gap height='30px' />
      <TextBlock
        text='가입하기'
        design={{
          ...design.createAuthButtonDesign(),
          box: {
            ...design.createAuthButtonDesign().box,
            backgroundColor: isSignupAvailable ? design.createAuthButtonDesign().box.backgroundColor : color.GRAY_1,
          },
        }}
        click={{ handler: handleSignUpClick, mode: 'throttle', delay: 3000 }}
      />
    </AuthBox>
  );
};

export default Signup;
